/**
 * 放送地点の新規作成 / 編集モーダル。
 *
 * - 地点情報フォーム + コンテンツ編集を **多段組グリッド** (`.form-grid`) に配置する。
 *   3 段組時の並びは 1 段目「説明 / 緯度 / 経度」・2 段目「自線区愛称 / 半径 / 放送遅延」で、
 *   画面幅に応じて 2 段 / 1 段へ自動追従して縦スクロール量を抑える。
 * - 放送遅延は「判定された GPS 圏内に入ってから何秒後に放送するか」であることが
 *   ラベルだけでは伝わらないため、入力欄の下に補足ヒントを表示する。
 * - モーダル自体のサイズは「大きすぎる」という指摘を受けて画面いっぱいをやめ、
 *   `min(1100px, 90vw)` / `max-height: 86vh` (styles.css `.modal--single-column`) に収める。

 * - 地図は「📍 地図を開く」で **モーダル本体と同サイズのポップアップ** として重ねて表示する
 *   (ピン + 半径円。ドラッグまたはクリックで位置指定)。
 *   地図で指定した座標は入力欄へ即時反映され、フッターの「この位置を使う」/ × / Esc の
 *   いずれで閉じても座標は保持される (保存は編集画面の「保存」ボタンで行う)。
 * - モーダル背景 (`.modal-backdrop`) は背後の一覧テーブル / 地図をぼかして、
 *   編集作業に視線を集中させる。
 * - 半径スライダー操作で地図上の Circle がリアルタイムに同期する。
 * - spotId は UI 上に一切表示せず、編集対象を識別する内部値としてのみ保持する

 *   (新規作成時は undefined で、API 側が UUID v7 を採番する)。
 * - 1 地点に対して複数のコンテンツを設定可能。カテゴリ毎に必要な入力フィールドが
 *   動的に切り替わる。
 * - 保存ボタンで親に値を渡し、親側で API 呼び出し (POST/PUT) を実行する。
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import type { BroadcastContent, BroadcastPoint, Chime } from '../api/types';
import type { GtfsStop } from '../data/gtfsStops';
import type { IStationsLine } from '../data/istationsLines';

import { POLLY_VOICE_OPTIONS } from '../data/pollyVoices';

import { parseRouteDirection, toRouteDirection, type RouteMaster } from '../data/routes';
import { fetchRouteShapePoints, type RouteShapePointsFile } from '../data/routeShapes';
import { findStationSegment, formatStationSegmentLabel, type RouteStop } from '../data/routeStops';
import { validateContent } from '../utils/broadcastContents';
import { playSequential, type SequentialAudioItem } from '../utils/sequentialAudio';

import { BroadcastContentsEditor, type PreviewSsmlHandler } from './BroadcastContentsEditor';
import { usePreviewSample } from './broadcastContents/hooks/usePreviewSample';
import { PreviewSamplePanel } from './broadcastContents/parts/PreviewSamplePanel';
import {
  buildPointSampleContext,
  derivePointSampleOverrides,
  resolvePointSampleFields,
} from './broadcastContents/previewSample';
import { BroadcastPointsMap } from './BroadcastPointsMap';
import { ConfirmDialog } from './ConfirmDialog';
// 数値入力は共通の NumericInput を使う (0 が消せない不具合の共通対策)。
import { clampNonNegativeInt, NumericInput } from './NumericInput';

export interface BroadcastPointFormValues {
  routeDirection: string;
  /**
   * 内部的にのみ保持する spotId。UI 上には表示されず、API への識別子として渡す。
   * 新規作成時は undefined。
   */
  spotId?: string;
  description: string;
  latitude: number | undefined;
  longitude: number | undefined;
  radius_meters: number;
  broadcast_delay_seconds: number;
  /**
   * 自線区愛称 (任意)。自路線を放送 SSML の `{route_name}` / `{route}` に展開する際、
   * 空文字以外が設定されていれば **最優先** で使用する。
   *
   * UI 上のラベルは接続線側の「接続線 線区愛称」と区別できるよう
   * **「自線区愛称（任意）」** と表示する (フィールド名は API 互換のため `route_nickname`)。
   *
   * 空文字は「未設定扱い」で、`route_name` (Transformer routeName) → `f"{route_id}線"`
   * にフォールバックする。フォーム内では常に string で扱い、保存時に親側で
   * `undefined` へ変換する (BroadcastPointsPage.handleFormSubmit / handleImport)。
   */
  route_nickname: string;
  /**
   * 放送地点単位で切り替える Amazon Polly の VoiceId (日本語 Neural)。
   *
   * フォーム内では常に string で扱い (未設定は空文字)、保存時に親側で
   * 空文字→undefined に正規化する (BroadcastPointsPage.handleFormSubmit /
   * handleImport)。選択肢は `web-app/src/data/pollyVoices.ts` の
   * `POLLY_VOICE_OPTIONS` を参照。空文字はサーバ側デフォルト (`Takumi`) にフォールバック。
   *
   * 用途: Polly SSML 合成 (放送 / プレビュー) の声色切替。遅延判定等には影響しない。
   */
  voice_id: string;
  /**
   * 放送 **開始** 時に鳴らすチャイム音の `chime_id` (要件 3・4)。
   *
   * フォーム内では常に string で扱い (未設定 / 「(なし)」選択は空文字)、
   * 保存時に親側で空文字→undefined に正規化する
   * (BroadcastPointsPage.handleFormSubmit / handleImport)。
   *
   * 選択肢は ControlPanel API `GET /chimes` (チャイムマスタ) から取得した
   * {@link BroadcastPointFormProps.chimes} を利用する。空文字は「開始チャイムを
   * 鳴らさない」を意味する (既存挙動と完全に同一)。
   */
  start_chime_id: string;
  /**
   * 放送 **終了** 時に鳴らすチャイム音の `chime_id` (要件 3・4)。
   * 仕様は {@link start_chime_id} と同じ (差し込み位置が放送キューの末尾になる)。
   */
  end_chime_id: string;
  contents: BroadcastContent[];
}

export interface BroadcastPointFormProps {
  /** モーダルのタイトルに表示するモード */
  mode: 'create' | 'edit';
  /** 編集対象 (新規作成時は routeDirection のみ与える) */
  initialValue: BroadcastPointFormValues;
  /** 同 routeDirection の他の地点 (地図にグレーで表示) */
  otherPoints: BroadcastPoint[];
  /**
   * 親が既に取得済みの当該方面の線形 GeoJSON (Point Feature の集合)。
   * - 親側 (BroadcastPointsPage) で選択中の routeDirection に対応する GeoJSON が
   *   ここに渡される。`null` の場合、フォーム側で `initialValue.routeDirection`
   *   から自前で fetch する (= 全件 scan モードのテーブル経由で編集を開いたケース)。
   */
  routeShapePoints: RouteShapePointsFile | null;
  /**
   * `routeShapePoints` がどの routeDirection 用に取得されたものかを示す。
   * - 「コピーして新規作成」でコピー元と異なる線区/方面を選択した場合、
   *   親 (BroadcastPointsPage) は画面上で選択中の routeDirection に対応する
   *   `routeShapePoints` をそのまま渡してくるが、編集対象の routeDirection は
   *   既にコピー先のものになっている。
   * - そのため親値をそのまま地図に渡すと、コピー元路線の線形 (強調表示・GPS 強度・
   *   定型放送区間) が描画されてしまう。
   * - 本フィールドが `values.routeDirection` と一致しないときは、親値を採用せず
   *   フォーム側で自前 fetch した値 (`localShapePoints`) を使う。
   */
  routeShapePointsRouteDirection?: string;

  /**
   * 線区マスタ。接続線遅延コンテンツの route_id ドロップダウンに使用する。
   * 省略時は空配列扱い。
   */
  routes?: RouteMaster[];
  /**
   * 駅順マスタ (route_id → 駅一覧)。接続線遅延コンテンツの開始駅 / 終了駅ドロップダウン用。
   * 親 (BroadcastPointsPage) が ControlPanel API 経由で事前取得して渡す。省略時は空。
   */
  routeStops?: Record<string, RouteStop[]>;
  /**
   * 接続線遅延コンテンツの「運行情報の参照元（アイステイションズ路線）」複数選択用のマスタ。
   * ATOS 線区マスタ (`routes`) とは独立した別系統で、連動させない。
   * 親 (BroadcastPointsPage) が `fetchIStationsLines()` で事前取得して渡す。
   */
  istationsLines?: IStationsLine[];
  /**
   * GTFS 静的 stops (親駅 + 子番線) の全件。任意テキストの放送条件「停車番線」の
   * 番線ドロップダウン用。`route_id` で絞り込めないため
   * 親 (BroadcastPointsPage) が `getGtfsStops()` で 1 回だけ全件取得して渡す。
   * 省略時は空配列として扱い、番線セレクトは無効化される (既存の選択値は残す)。
   */
  gtfsStops?: GtfsStop[];
  /**

   * チャイム音マスタ (`GET /chimes`) の一覧 (要件 3・4)。

   * 放送開始 / 終了チャイムのセレクタの選択肢として使う。省略時は空配列扱いで、
   * セレクタは「(なし)」のみとなる (= チャイム未設定)。
   */
  chimes?: Chime[];
  /**
   * 選択中チャイムの試聴処理 (`GET /chimes/{chime_id}/audio`) (要件 3・4)。
   * chime_id を受け取り、再生可能な object URL (または data URL) を返す。
   * 省略時は試聴ボタンを表示しない。
   */
  onPreviewChime?: (chimeId: string) => Promise<string>;
  /**
   * 任意テキスト (free_text) コンテンツの音声ファイルアップロード処理。
   * MP3 File を受け取り、保存された audio_key を返す。省略時はアップロード UI を無効化する。
   */
  onUploadAudio?: (file: File) => Promise<string>;
  /**
   * SSML / 任意テキストの音声プレビュー処理。省略時はプレビュー UI を無効化する。
   */
  onPreviewSsml?: PreviewSsmlHandler;
  /** 保存処理 (Promise を返す。throw でエラー表示) */
  onSubmit: (values: BroadcastPointFormValues) => Promise<void>;
  /**
   * 「コピーして新規作成」ボタン押下 → コピー先選択ダイアログで「実行」が押された時のハンドラ。
   *
   * @param values モーダル内で編集中の最新値 (説明・緯度・経度はこの後親側でクリアされる想定)
   * @param routeDirection ユーザーがコピー先として選択した `${routeId}#${directionName}`
   *
   * 親側で新規作成モードのモーダルを再オープンすることを想定する。
   * 省略 / mode === 'create' のときはコピーボタン自体が表示されない。
   */
  onCopy?: (values: BroadcastPointFormValues, routeDirection: string) => void;
  /** キャンセル */
  onClose: () => void;
}

const DEFAULT_RADIUS = 100;

/**
 * 半径 (m) のワンクリックプリセット。
 *
 * 現場 WG からの要望で「よく使う半径はスライダーを動かさずワンクリックで設定したい」ため、
 * スライダーの横にプリセットボタンを並べる (スライダーによる任意値指定も従来通り可能)。
 */
const RADIUS_PRESETS: readonly number[] = [100, 150, 200];

/**
 * 地図ポップアップの初期サイズ・位置のフォールバック値算出。
 *
 * 基本方針は「編集モーダル本体と同じ大きさ・同じ位置に重ねて表示する」。
 * モーダルの実寸は `getBoundingClientRect()` で取得するが、まだレイアウトが
 * 確定していない (jsdom など幅 0 になる) 場合はこのフォールバックを使う。
 *
 * NOTE: モジュールスコープで `window` を参照すると、テスト環境や
 * ウィンドウリサイズ後に古い値が固定されてしまうため、必ず呼び出し時に評価する。
 */
function fallbackPopupRect(): { width: number; height: number; top: number; left: number } {
  // モーダル本体 (.modal--single-column: width min(1100px, 90vw) / max-height 86vh)
  // と同等の見え方になる値を使う。
  const width = Math.min(1100, Math.round(window.innerWidth * 0.9));
  const height = Math.round(window.innerHeight * 0.86);

  return {
    width,
    height,
    top: Math.max(8, Math.round((window.innerHeight - height) / 2)),
    left: Math.max(8, Math.round((window.innerWidth - width) / 2)),
  };
}

export function BroadcastPointForm(props: BroadcastPointFormProps): React.JSX.Element {
  const {
    mode,
    initialValue,
    otherPoints,
    routeShapePoints,
    routeShapePointsRouteDirection,
    routes,
    routeStops,
    istationsLines,
    gtfsStops,
    chimes,

    onPreviewChime,
    onUploadAudio,
    onPreviewSsml,
    onSubmit,
    onCopy,
    onClose,
  } = props;

  const [values, setValues] = useState<BroadcastPointFormValues>(() => normalize(initialValue));

  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState<string | null>(null);
  /** 地図ポップアップの表示状態 */
  const [mapOpen, setMapOpen] = useState(false);
  /** 「地図を開く」ボタンへの ref (ポップアップの位置計算に使う) */
  const mapButtonRef = useRef<HTMLButtonElement>(null);
  /**
   * モーダル本体（スクロール領域）への ref。
   * 「コピーして新規作成」など initialValue 切り替え時に
   * scrollTop=0 へ戻して、コピー元のスクロール位置が引き継がれるのを防ぐ。
   */
  const modalBodyRef = useRef<HTMLDivElement>(null);
  /**
   * 「コピーして新規作成」ダイアログの表示状態。
   * true のとき、コピー先の線区/方面を選択する小さなダイアログがモーダル上に重ねて表示される。
   */
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  /**
   * 「コピーして新規作成」押下時の未保存変更確認ダイアログの表示状態。
   * window.confirm の代わりにアプリ独自の ConfirmDialog を表示するために使う。
   */
  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);

  /**
   * チャイム試聴の状態 (要件 3・4)。
   * - `playing`: 現在試聴処理中の役割 (`'start'` / `'end'`)。多重クリック抑止に使う。
   * - `error`: 試聴に失敗した場合のメッセージ (保存処理のエラーとは分離して表示する)。
   */
  const [chimePreviewPlaying, setChimePreviewPlaying] = useState<'start' | 'end' | null>(null);
  const [chimePreviewError, setChimePreviewError] = useState<string | null>(null);

  /**
   * 放送地点まるごとプレビュー (通し聴き) の状態 (Issue #439)。
   *
   * - `playing`: 生成 + 連続再生中フラグ (多重クリック抑止)。
   * - `error`: 失敗メッセージ (保存エラーとは分離して表示し、保存操作をブロックしない)。
   * - `progress`: 「いま何を鳴らしているか」のラベル (例: 「#2 接続線遅延」)。
   */
  const [pointPreviewPlaying, setPointPreviewPlaying] = useState(false);
  const [pointPreviewError, setPointPreviewError] = useState<string | null>(null);
  const [pointPreviewProgress, setPointPreviewProgress] = useState<string | null>(null);

  /*
   * 放送地点まるごとプレビューの「プレビュー条件」(サンプル条件) — Issue #439。
   *
   * 個別フィールドのプレビュー (`AudioPreviewField`) と **同じ共通部品**
   * (`usePreviewSample` + `PreviewSamplePanel`) を使う。これにより
   *
   *   - 遅延の分 ⇔ 秒 換算
   *   - 「未編集のキーだけコンテンツ設定へ追従し、手入力は上書きしない」規則
   *   - 入力欄の出し分け (本番で読み上げない項目は出さない)
   *
   * が個別プレビューと完全に一致し、「画面によって指定できる条件が違う」という
   * 分かりにくさが生じない。
   *
   * 自動導出値は全コンテンツから畳み込む (`derivePointSampleOverrides`) ため、
   * コンテンツを編集するとサンプル遅延・乗換駅・方面も追従する
   * (ユーザーが条件パネルで明示指定した項目はそのまま尊重される)。
   */
  const pointSampleFields = resolvePointSampleFields(values.contents);
  const pointSampleHasFields =
    pointSampleFields.showDelay ||
    pointSampleFields.showRouteName ||
    pointSampleFields.showFromStation ||
    pointSampleFields.showTransferStation ||
    pointSampleFields.showDirectionName;
  const pointSample = usePreviewSample({
    initial: () => buildPointSampleContext(values.contents),
    overrides: derivePointSampleOverrides(values.contents),
  });

  // ---------------------------------------------------------------------------
  // モーダルドラッグ
  // ---------------------------------------------------------------------------
  const [modalPos, setModalPos] = useState<{ top: number; left: number } | null>(null);
  const modalDragRef = useRef<{
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleModalHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const el = modalRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    modalDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: rect.left,
      origTop: rect.top,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!modalDragRef.current) return;
      const dx = e.clientX - modalDragRef.current.startX;
      const dy = e.clientY - modalDragRef.current.startY;
      setModalPos({
        left: Math.max(0, modalDragRef.current.origLeft + dx),
        top: Math.max(0, modalDragRef.current.origTop + dy),
      });
    };
    const handleMouseUp = (): void => {
      modalDragRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  /**
   * 説明欄が「駅間判定による自動入力値」かどうか。
   * 新規作成時のみ、地図クリックで自動入力した直後は true となり、
   * 続けて地図を動かした際に説明を上書きしてよいと判断する。
   * ユーザーが説明を手入力で変更したら false に戻し、以後は自動上書きしない。
   *
   * NOTE: state ではなく ref で保持する。
   * 自動入力ロジックは `setValues` の関数更新コールバック内で参照されるが、
   * state を使うとクロージャに古い値がキャプチャされ、地図ドラッグ等で
   * 連続して `handleLocationChange` が呼ばれた際に 2 回目以降の上書きが
   * 効かなくなる (1 回目の `setDescriptionAutoFilled(true)` がまだ反映
   * されていないため `descriptionAutoFilled` が false のまま、かつ
   * `prev.description` は既に駅間ラベルになっており空ではないため、
   * `canAutoFill` 判定で false になってしまう)。
   * ref であれば同期的に値を更新でき、同一レンダー内の連続呼び出しでも
   * 正しく上書きが継続される。
   */
  const descriptionAutoFilledRef = useRef(false);

  /**
   * 編集対象の routeDirection に対応する線形 GeoJSON。
   * 親側 (BroadcastPointsPage) が同じ routeDirection 用に取得済みなら親値を使い、
   * それ以外 (例: 全件 scan モードのテーブルから別 routeDirection の編集を開いた場合) は
   * モーダル側で自前で取得する。
   */
  const [localShapePoints, setLocalShapePoints] = useState<RouteShapePointsFile | null>(null);

  // initialValue が変わった (= 別の地点を編集対象に切り替えた、または
  // 「コピーして新規作成」で同モーダルを再利用した) 場合のみリセット
  useEffect(() => {
    setValues(normalize(initialValue));
    setError(null);
    descriptionAutoFilledRef.current = false;

    // モーダル本体のスクロール位置を先頭に戻す。
    // モーダル DOM が再利用される (アンマウントされない) ケースでは、
    // ブラウザがスクロール位置を保持してしまうため明示的にリセットする。
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
  }, [initialValue]);

  /**
   * 親から渡された `routeShapePoints` が現在の編集対象 `values.routeDirection` と
   * 一致するかどうか。
   *
   * - 一致する場合 (= 通常の編集 / 同方面新規作成): 親値をそのまま採用する。
   * - 一致しない場合 (= 「コピーして新規作成」でコピー元と異なる線区/方面を選択した
   *   ケースなど): 親値は採用せず、フォーム側で自前 fetch した `localShapePoints` を使う。
   * - `routeShapePointsRouteDirection` が undefined のときは互換のため「親値があれば
   *   一致扱い」とする (props を渡してこない既存呼び出し元向け)。
   */
  const parentShapeMatches =
    routeShapePointsRouteDirection === undefined ||
    routeShapePointsRouteDirection === values.routeDirection;

  // 親から線形が渡されていない、または親値が現在の routeDirection と
  // 一致しない場合は、編集対象の routeDirection で自前 fetch する。
  useEffect(() => {
    if (routeShapePoints && parentShapeMatches) {
      // 親値をそのまま使うのでローカル取得値は不要
      setLocalShapePoints(null);
      return;
    }
    const { routeId, directionName } = parseRouteDirection(values.routeDirection);
    if (!routeId || !directionName) {
      setLocalShapePoints(null);
      return;
    }
    let aborted = false;
    void (async () => {
      const data = await fetchRouteShapePoints(routeId, directionName);
      if (!aborted) setLocalShapePoints(data);
    })();
    return () => {
      aborted = true;
    };
  }, [routeShapePoints, parentShapeMatches, values.routeDirection]);

  // 親値が現在の routeDirection 用なら優先、そうでなければローカル取得値を地図に渡す。
  const effectiveShapePoints = parentShapeMatches
    ? (routeShapePoints ?? localShapePoints)
    : localShapePoints;

  /**
   * 緯度経度から駅間ラベルを判定して、新規作成時に説明欄へ自動入力するための共通処理。
   *
   * - 自動入力は新規作成時 (`mode === 'create'`) のみ。
   * - 説明が空 または 直前に自動入力した値の場合のみ上書きする (手入力は尊重)。
   * - 緯度経度が未確定 / 不正な場合は何もしない。
   *
   * 引数の values から次状態を返し、必要に応じて descriptionAutoFilled も更新する。
   */
  const applyAutoDescription = (
    next: BroadcastPointFormValues,
    prev: BroadcastPointFormValues,
  ): BroadcastPointFormValues => {
    if (mode !== 'create') return next;
    // 説明が空 または 自動入力済みの場合のみ上書き対象とする (手入力は尊重)。
    // ref を使うことで、`setValues` 関数更新コールバック内でも常に最新の
    // 「直前に自動入力したか」を参照できる (state クロージャ問題を回避)。
    const canAutoFill = descriptionAutoFilledRef.current || prev.description.trim() === '';
    if (!canAutoFill) return next;

    const label = computeStationSegmentLabel(
      routeStops,
      prev.routeDirection,
      next.latitude,
      next.longitude,
    );
    if (!label) return next;

    // 自動入力したことを記録し、続けて緯度経度を変更しても上書きできるようにする。
    descriptionAutoFilledRef.current = true;
    return { ...next, description: label };
  };

  /**
   * 編集モーダルの内容が初期値から変更されているか (= 未保存の変更があるか)。
   *
   * - `initialValue` を `normalize` して `values` と JSON 文字列で比較する簡易判定。
   * - フィールド数・型が固定で順序も安定しているため、`JSON.stringify` 比較で
   *   実用上十分な精度が得られる。
   * - 「コピーして新規作成」押下時に未保存変更を警告するために使用する。
   */
  const isDirty = useMemo(
    () => JSON.stringify(normalize(initialValue)) !== JSON.stringify(values),
    [initialValue, values],
  );

  const handleChange = <K extends keyof BroadcastPointFormValues>(
    key: K,
    value: BroadcastPointFormValues[K],
  ): void => {
    // 説明をユーザーが手入力で変更したら、以後は自動上書きを止める。
    if (key === 'description') {
      descriptionAutoFilledRef.current = false;
    }
    setValues((v) => {
      const next = { ...v, [key]: value };
      // 緯度 / 経度が変わった場合は駅間自動入力を試みる (新規作成時のみ)。
      if (key === 'latitude' || key === 'longitude') {
        return applyAutoDescription(next, v);
      }
      return next;
    });
  };

  /**
   * 地図上で位置が指定された (クリック / ドラッグ) ときのハンドラ。
   *
   * - 緯度経度を常に更新する。
   * - 新規作成時 (`mode === 'create'`) かつ、説明欄が空 または 直前に自動入力した値の
   *   場合のみ、駅順マスタから「開始駅-終了駅」を判定して説明欄へ自動セットする。
   *   ユーザーが手入力した説明は尊重して上書きしない。
   */
  const handleLocationChange = (lat: number, lng: number): void => {
    setValues((v) => {
      const next = { ...v, latitude: lat, longitude: lng };
      return applyAutoDescription(next, v);
    });
  };

  /**
   * 選択中のチャイムを試聴する (要件 3・4)。
   *
   * `onPreviewChime` (= `GET /chimes/{chime_id}/audio`) で取得した再生用 URL を
   * `Audio` 要素で再生する。試聴失敗は本フォームの保存エラーとは別枠で表示し、
   * 保存操作をブロックしない (チャイム設定そのものは選択済みの chime_id で成立する)。
   */
  const handlePreviewChime = async (role: 'start' | 'end'): Promise<void> => {
    if (!onPreviewChime) return;
    const chimeId = role === 'start' ? values.start_chime_id : values.end_chime_id;
    if (!chimeId) return;
    setChimePreviewError(null);
    setChimePreviewPlaying(role);
    try {
      const url = await onPreviewChime(chimeId);
      const audio = new Audio(url);
      await audio.play();
    } catch (err) {
      setChimePreviewError((err as Error).message || 'チャイムの試聴に失敗しました');
    } finally {
      setChimePreviewPlaying(null);
    }
  };

  /**
   * 放送地点まるごとプレビュー (通し聴き) — Issue #439。
   *
   * 実際の放送キュー順に音声を生成してから連続再生する。
   *
   *   1. 開始チャイム (`start_chime_id` → `GET /chimes/{chime_id}/audio`)
   *   2. `contents[]` を並び順に (各コンテンツの `playback_rate` を要素ごとに反映)
   *   3. 終了チャイム (`end_chime_id`)
   *
   * サーバ側で ffmpeg 結合する必要はなく、`<audio>` の `ended` を繋ぐ
   * プレイリスト再生 (`playSequential`) で十分。
   *
   * 条件付きコンテンツ (`conditions` 付きの free_text や遅延しきい値未満のもの) は
   * 「条件を満たしたものとして鳴らす」扱いにする (サーバ側のサンプル条件で合成される)。
   * 生成に失敗したコンテンツはスキップし、最後にまとめて警告する
   * (1 件の設定ミスで通し聴き全体が不可能になるのを避けるため)。
   */
  const handlePreviewWholePoint = async (): Promise<void> => {
    if (!onPreviewSsml || pointPreviewPlaying) return;
    setPointPreviewError(null);
    setPointPreviewPlaying(true);
    setPointPreviewProgress('音声を生成しています...');
    const skipped: string[] = [];
    // 「プレビュー条件」パネルで指定した (または未指定なら自動導出した) サンプル条件。
    // 個別プレビューと同じ解決規則 (`usePreviewSample`) を通るため、
    // 「条件パネルに出ている値」と「実際に鳴る音声」が食い違わない。
    const sampleContext = pointSample.resolve();
    try {
      const items: SequentialAudioItem[] = [];

      // 1. 開始チャイム
      if (values.start_chime_id && onPreviewChime) {
        try {
          items.push({
            url: await onPreviewChime(values.start_chime_id),
            label: '開始チャイム',
          });
        } catch {
          skipped.push('開始チャイム');
        }
      }

      // 2. コンテンツ本体 (並び順)
      for (const [index, content] of values.contents.entries()) {
        const label = `#${index + 1} ${content.category}`;
        try {
          const result = await onPreviewSsml({
            content,
            voiceId: values.voice_id || undefined,
            // Issue #439: 通し試聴でもプレビュー条件 (サンプル遅延・線名・乗換駅・方面) を
            // サーバへ送る。これが無いとサーバ側の既定サンプル (5 分固定) で鳴ってしまい、
            // 「境界値でどのパターンが鳴るか」を通しで確認できない。
            sampleContext,
          });
          items.push({
            url: result.url,
            // 端末側と同じく、要素ごとに再生速度を適用する。
            playbackRate: content.playback_rate,
            label,
          });
        } catch {
          // 必須項目未入力 / サンプル遅延に該当パターン無しなどは 400 になる。
          // 通し聴き自体は続行し、最後にどれを飛ばしたか伝える。
          skipped.push(label);
        }
      }

      // 3. 終了チャイム
      if (values.end_chime_id && onPreviewChime) {
        try {
          items.push({
            url: await onPreviewChime(values.end_chime_id),
            label: '終了チャイム',
          });
        } catch {
          skipped.push('終了チャイム');
        }
      }

      if (items.length === 0) {
        setPointPreviewError('通しで試聴できる放送内容がありません。設定を確認してください。');
        return;
      }

      setPointPreviewProgress(`${items.length} 件を順番に再生します...`);
      await playSequential(items);
      if (skipped.length > 0) {
        setPointPreviewError(
          `一部を試聴できませんでした (設定を確認してください): ${skipped.join(', ')}`,
        );
      }
    } catch (err) {
      setPointPreviewError((err as Error).message || '通し試聴に失敗しました');
    } finally {
      setPointPreviewPlaying(false);
      setPointPreviewProgress(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    // バリデーション
    const validationError = validate(values);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(values);
    } catch (err) {
      setError((err as Error).message || '保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="broadcast-point-form-modal"
      data-mode={mode}
    >
      <div
        ref={modalRef}
        className="modal modal--single-column"
        style={
          modalPos
            ? { position: 'fixed', top: modalPos.top, left: modalPos.left, transform: 'none' }
            : undefined
        }
      >
        <div
          className="modal-header"
          onMouseDown={handleModalHeaderMouseDown}
          style={{ cursor: 'grab' }}
        >
          <h2 style={{ margin: 0, fontSize: 15 }}>
            {mode === 'create' ? '放送地点を新規作成' : '放送地点を編集'} ({values.routeDirection})
          </h2>
          <button
            type="button"
            data-testid="modal-close-button"
            onClick={onClose}
            disabled={submitting}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          <div ref={modalBodyRef} className="modal-body modal-body--single-column">
            <div className="form-pane">
              <p className="form-required-hint">
                <span className="required-mark">*</span> は必須項目です
              </p>
              {/*
                入力欄の多段組レイアウト。
                以前は全項目が 1 行フル幅を占有しており、緯度・半径・放送遅延など
                短い値の欄まで極端に横長になっていた。入力内容の長さに応じて
                意味のまとまり単位で 1 セル (または複数セル) へ振り分ける。

                現場 WG 要望による段組の並び (3 段組時):
                  1 段目: 説明 / 緯度 / 経度 (+ 📍 地図を開くボタン)
                  2 段目: 自線区愛称 / 半径 / 放送遅延
                緯度・経度は 1 つの `.form-grid__inline` セルにまとめているため、
                1 段目で残り 2 列ぶんを占有するよう `.form-grid__span2` を付与する。

                列数は `.form-grid` の CSS 側で画面幅に応じて 2 段 / 3 段へ自動追従し、
                縦スクロール量を抑える。

                「どの項目がどの項目と紐づいているのか分かりにくい」という指摘を受けて、
                各セル (= 1 つの設定のまとまり) に `.form-grid__group` を付けて
                背景を薄いグレーで塗り分ける。
                例: 「緯度 / 経度 / 📍地図を開く」や「半径スライダー / 半径プリセット」は
                それぞれ 1 つの設定なので、同じグレーの枠の中に収まって見える。
              */}
              <div className="form-grid" data-testid="broadcast-point-form-grid">
                <label className="form-grid__group">
                  <span className="required">説明</span>
                  <input
                    type="text"
                    data-testid="description-input"
                    aria-label="説明"
                    value={values.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="例: 放送位置(代々木→新宿駅)"
                    required
                  />
                </label>

                {/*
                  緯度・経度 + 地図ボタン (セル内で横並び)。
                  1 段目を「説明 / 緯度 / 経度」に揃えるため、残り 2 列ぶんを占有する。
                */}
                <div className="form-grid__inline form-grid__span2 form-grid__group">
                  <label>
                    <span className="required">緯度</span>
                    <input
                      type="number"
                      step="0.000001"
                      data-testid="latitude-input"
                      aria-label="緯度"
                      placeholder="緯度"
                      value={values.latitude ?? ''}
                      onChange={(e) =>
                        handleChange(
                          'latitude',
                          e.target.value === '' ? undefined : Number(e.target.value),
                        )
                      }
                      required
                    />
                  </label>
                  <label>
                    <span className="required">経度</span>
                    <input
                      type="number"
                      step="0.000001"
                      data-testid="longitude-input"
                      aria-label="経度"
                      placeholder="経度"
                      value={values.longitude ?? ''}
                      onChange={(e) =>
                        handleChange(
                          'longitude',
                          e.target.value === '' ? undefined : Number(e.target.value),
                        )
                      }
                      required
                    />
                  </label>
                  {/* 地図を開くボタン */}
                  <button
                    type="button"
                    ref={mapButtonRef}
                    className={mapOpen ? 'primary' : ''}
                    aria-label="地図で位置を選択"
                    aria-expanded={mapOpen}
                    onClick={() => setMapOpen((o) => !o)}
                    style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                  >
                    📍 地図{mapOpen ? 'を閉じる' : 'を開く'}
                  </button>
                </div>

                {/*
                  2 段目の先頭。自線区 (この放送地点が属する路線) の愛称。
                  接続線コンテンツ側の「接続線 線区愛称（任意）」と混同しないよう、
                  ラベル / aria-label をいずれも「自線区愛称」に統一する。
                  (data-testid は既存テスト・E2E 互換のため route-nickname-input を維持)
                */}
                <label className="form-grid__group">
                  <span>自線区愛称（任意）</span>
                  <input
                    type="text"
                    data-testid="route-nickname-input"
                    aria-label="自線区愛称"
                    value={values.route_nickname}
                    onChange={(e) => handleChange('route_nickname', e.target.value)}
                    placeholder="例: 湘南新宿ライン"
                  />
                  <span className="hint" style={{ fontSize: 12, color: '#555' }}>
                    自路線の放送で <code>{'{route_name}'}</code> / <code>{'{route}'}</code>{' '}
                    プレースホルダに最優先で使用されます。
                    未入力の場合は従来通り一般的な線名で案内します。
                  </span>
                </label>

                {/*
                  Polly VoiceId (音声) セレクタ。放送 SSML 合成の声色を放送地点単位で
                  切り替えるための選択項目。未選択 (空文字) はサーバー側デフォルト
                  (Takumi) にフォールバックする。
                  選択肢は `web-app/src/data/pollyVoices.ts` の POLLY_VOICE_OPTIONS を参照。

                  プレースホルダ (value="") のラベルには具体的な声色名 (Takumi) を
                  出さない。以前は `(デフォルト: Takumi)` としていたが、その下に
                  `Takumi（男性・日本語 Neural）` の option も並ぶため、ユーザーから
                  見ると「Takumi 相当の選択肢が 2 つある」ように見えて不自然だった。
                  「未選択 = 既定音声にフォールバック」というセマンティクスを保ちつつ、
                  具体的な既定音声の名称は下のヒント文にのみ表示する。
                */}
                <label className="form-grid__group">
                  <span>音声（任意）</span>
                  <select
                    data-testid="voice-id-select"
                    aria-label="音声"
                    value={values.voice_id}
                    onChange={(e) => handleChange('voice_id', e.target.value)}
                  >
                    <option value="">（未選択 / 既定音声を使用）</option>
                    {POLLY_VOICE_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span className="hint" style={{ fontSize: 12, color: '#555' }}>
                    この放送地点の Polly 合成音声 (声色) を切り替えられます。
                    未選択の場合は既定の音声 (Takumi) を使用します。
                  </span>
                </label>

                <label className="form-grid__group">
                  <span className="required">半径 (m): {values.radius_meters}</span>
                  <input
                    type="range"
                    min={10}
                    max={1000}
                    step={5}
                    data-testid="radius-input"
                    aria-label="半径"
                    value={values.radius_meters}
                    onChange={(e) => handleChange('radius_meters', Number(e.target.value))}
                  />
                  {/*
                    よく使う半径のワンクリックプリセット (現場 WG 要望)。
                    スライダーでの任意値指定も従来どおり可能で、現在値と一致するボタンは
                    `is-active` クラスでハイライトする。
                  */}
                  <div className="radius-presets" data-testid="radius-presets">
                    {RADIUS_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        className={`radius-preset${values.radius_meters === preset ? ' is-active' : ''}`}
                        data-testid={`radius-preset-${preset}`}
                        aria-label={`半径 ${preset}m を設定`}
                        aria-pressed={values.radius_meters === preset}
                        onClick={() => handleChange('radius_meters', preset)}
                      >
                        {preset}m
                      </button>
                    ))}
                  </div>
                </label>

                <label className="form-grid__group">
                  <span className="required">放送遅延 (秒)</span>
                  {/*
                    NumericInput が内部で「入力中の文字列」を保持し、一時的な空欄を許容する。
                    素の <input type="number" value={number}> だと 0 を消した瞬間
                    Number('') === 0 で 0 が復活してしまい「0 が消せない」不具合になる。
                    空欄のまま blur した場合は 0 表示に戻って保存値と一致する。
                  */}
                  <NumericInput
                    min={0}
                    step={1}
                    className="input--numeric"
                    data-testid="broadcast-delay-input"
                    aria-label="放送遅延"
                    value={values.broadcast_delay_seconds}
                    normalizeValue={clampNonNegativeInt}
                    onChange={(delaySeconds) =>
                      handleChange('broadcast_delay_seconds', delaySeconds)
                    }
                  />
                  {/*
                    「放送遅延」だけでは何を遅延させる秒数なのか分からないという指摘があったため、
                    「GPS 圏内 (緯度・経度 + 半径) に進入してから放送が流れるまでの待ち時間」で
                    あることを補足する。
                  */}
                  <span
                    className="hint"
                    data-testid="broadcast-delay-hint"
                    style={{ fontSize: 12, color: '#555' }}
                  >
                    上記の緯度・経度と半径で判定される GPS 圏内に入ったあと、
                    何秒後に放送を流すかを設定できます。0 の場合は圏内に入った時点で即時放送します。
                  </span>
                </label>

                {/*
                  要件 3・4: 放送開始 / 終了チャイム音のセレクタ (放送地点単位)。

                  - 選択肢はチャイムマスタ (`GET /chimes`) の一覧。「(なし)」を選ぶと
                    そのタイミングではチャイムを鳴らさない (= 既存挙動と完全に同じ)。
                  - サーバー側 (UpdateContents) は選択されたチャイム音源を
                    `contents_schedule[]` の先頭 / 末尾に `is_chime: true` エントリとして
                    差し込む。既存音源との合成 (ffmpeg) は行わない。
                  - 選択済みの場合は「▶ 試聴」ボタンで音を確認できる
                    (`GET /chimes/{chime_id}/audio`)。
                */}
                <ChimeSelectField
                  role="start"
                  label="放送開始チャイム（任意）"
                  value={values.start_chime_id}
                  chimes={chimes ?? []}
                  previewing={chimePreviewPlaying === 'start'}
                  canPreview={Boolean(onPreviewChime)}
                  onChange={(chimeId) => handleChange('start_chime_id', chimeId)}
                  onPreview={() => void handlePreviewChime('start')}
                />
                <ChimeSelectField
                  role="end"
                  label="放送終了チャイム（任意）"
                  value={values.end_chime_id}
                  chimes={chimes ?? []}
                  previewing={chimePreviewPlaying === 'end'}
                  canPreview={Boolean(onPreviewChime)}
                  onChange={(chimeId) => handleChange('end_chime_id', chimeId)}
                  onPreview={() => void handlePreviewChime('end')}
                />
              </div>

              {chimePreviewError ? (
                <div
                  className="error-message"
                  role="alert"
                  data-testid="chime-preview-error"
                  style={{ marginTop: 8 }}
                >
                  {chimePreviewError}
                </div>
              ) : null}

              <BroadcastContentsEditor
                contents={values.contents}
                routes={routes}
                routeStops={routeStops}
                istationsLines={istationsLines}
                // 任意テキストの放送条件「停車番線」の番線ドロップダウン用の GTFS stops。
                // route_id では絞り込めないので、親ページが取得した全件をそのまま流す。
                gtfsStops={gtfsStops}
                // 自路線 (この地点が属する route_id)。BroadcastContentsEditor の

                // 「接続線遅延 (乗換駅別)」の乗換駅ドロップダウンで、親路線の
                // 駅一覧のみを候補として表示するために使う。
                // (routeStops は全 route_id ぶんが Record で入っているので、
                //  自路線に絞り込むにはこの値が必要)
                parentRouteId={parseRouteDirection(values.routeDirection).routeId}
                // 音声プレビュー (POST /audio-preview) に流し込む Polly VoiceId。
                // 空文字はサーバー側デフォルト (Takumi) にフォールバックする。
                parentVoiceId={values.voice_id || undefined}
                onUploadAudio={onUploadAudio}
                onPreviewSsml={onPreviewSsml}
                onChange={(contents) => handleChange('contents', contents)}
              />

              {/*
                Issue #439: 放送地点まるごとプレビュー (通し聴き)。
                「開始チャイム → contents[] を並び順に → 終了チャイム」を実際の放送キュー順に
                連続再生する。個別プレビューでは絶対に気付けない
                「チャイムと本文の間が詰まりすぎ/空きすぎ」「コンテンツの並び順が変」を発見できる。
              */}
              {onPreviewSsml ? (
                <div
                  className="point-preview-field"
                  data-testid="point-preview-field"
                  style={{ marginTop: 12 }}
                >
                  <button
                    type="button"
                    data-testid="point-preview-button"
                    aria-label="この放送地点を通しで試聴"
                    disabled={pointPreviewPlaying || values.contents.length === 0}
                    title={
                      values.contents.length === 0
                        ? 'コンテンツを追加すると通しで試聴できます'
                        : undefined
                    }
                    onClick={() => void handlePreviewWholePoint()}
                  >
                    {pointPreviewPlaying ? '再生中...' : '🔊 この放送地点を通しで試聴'}
                  </button>
                  {/*
                    Issue #439: 通し試聴でも「プレビュー条件」を指定できるようにする。
                    個別フィールドのプレビューと同じトグル + 同じパネル部品を使うため、
                    操作方法・入力欄・遅延の分/秒換算がまったく同じになる。
                    使えるプレースホルダを持つコンテンツが 1 件も無い場合
                    (未設定 / 任意テキストのみ) はトグル自体を出さない。
                  */}
                  {pointSampleHasFields ? (
                    <button
                      type="button"
                      aria-expanded={pointSample.open}
                      aria-label="プレビュー条件 (point)"
                      data-testid="preview-sample-toggle-point"
                      style={{ marginLeft: 8 }}
                      onClick={pointSample.toggle}
                    >
                      {pointSample.open ? '▼' : '▶'} プレビュー条件
                    </button>
                  ) : null}
                  <span className="hint" style={{ marginLeft: 8 }}>
                    開始チャイム → コンテンツ (並び順) → 終了チャイム の順に再生します。
                    条件付きコンテンツは「条件成立時のみ放送」されるものも含めて鳴らします。
                  </span>
                  {pointSample.open && pointSampleHasFields ? (
                    <PreviewSamplePanel
                      testIdSuffix="point"
                      fields={pointSampleFields}
                      sample={pointSample.shown}
                      onChange={pointSample.update}
                    />
                  ) : null}
                  {pointPreviewProgress ? (
                    <div
                      className="hint"
                      data-testid="point-preview-progress"
                      aria-live="polite"
                      style={{ marginTop: 4 }}
                    >
                      {pointPreviewProgress}
                    </div>
                  ) : null}
                  {pointPreviewError ? (
                    <div
                      className="error-message"
                      role="alert"
                      data-testid="point-preview-error"
                      style={{ marginTop: 4 }}
                    >
                      {pointPreviewError}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="modal-footer">
            {error ? (
              <div
                className="error-message"
                role="alert"
                style={{ flex: 1, margin: 0, textAlign: 'left' }}
              >
                {error}
              </div>
            ) : null}
            <button
              type="button"
              data-testid="cancel-button"
              onClick={onClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            {mode === 'edit' && onCopy ? (
              <button
                type="button"
                data-testid="copy-button"
                onClick={() => {
                  // 編集モーダル内に未保存の変更がある場合は、コピー先選択ダイアログを開く前に
                  // 「変更が保存されないけど続行して良いか」を確認する。
                  // (コピーは編集元には反映されないため、ユーザーが意図的に破棄するか確認させる)
                  if (isDirty) {
                    setUnsavedConfirmOpen(true);
                    return;
                  }
                  setCopyDialogOpen(true);
                }}
                disabled={submitting}
                aria-label="この内容をコピーして新規作成"
                title="現在の内容を引き継いで新規の放送地点を作成します（コピー先の線区/方面を選択できます。未保存の変更がある場合は確認ダイアログが表示されます）"
              >
                📋 コピーして新規作成
              </button>
            ) : null}
            <button
              type="submit"
              data-testid="save-button"
              className="primary"
              disabled={submitting}
            >
              {submitting ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>

      {/* 地図ポップアップ */}
      {mapOpen ? (
        <MapPopup
          // 「地図表示画面が小さい」という指摘に対応して、編集モーダル本体と
          // 同じ大きさ・同じ位置に重ねて表示する (実寸は開いた時点で計測する)。
          modalRef={modalRef}
          onClose={() => setMapOpen(false)}
          editingLatitude={values.latitude}
          editingLongitude={values.longitude}
          editingRadiusMeters={values.radius_meters || DEFAULT_RADIUS}
          otherPoints={otherPoints.filter(
            // 自分自身は除外し、かつ現在編集中の routeDirection と一致するもののみを残す。
            // 後者は「コピーして新規作成」でコピー元と異なる線区/方面を選択したケースで、
            // 親から渡された otherPoints がコピー元路線のままになっているのを防ぐための
            // 防御的フィルタ (通常編集時は親側で既に絞り込み済みのため副作用なし)。
            (p) => p.spotId !== values.spotId && p.routeDirection === values.routeDirection,
          )}
          routeShapePoints={effectiveShapePoints}
          onLocationChange={handleLocationChange}
        />
      ) : null}

      {/* コピー先選択ダイアログ */}
      {copyDialogOpen && onCopy ? (
        <CopyDialog
          routes={routes ?? []}
          sourceRouteDirection={values.routeDirection}
          onClose={() => setCopyDialogOpen(false)}
          onConfirm={(routeDirection) => {
            setCopyDialogOpen(false);
            onCopy(values, routeDirection);
          }}
        />
      ) : null}

      {/* 「コピーして新規作成」押下時の未保存変更確認ダイアログ */}
      <ConfirmDialog
        open={unsavedConfirmOpen}
        title="未保存の変更があります"
        message={
          '本編集画面内で変更された内容が保存されていません。\n' +
          '「コピーして新規作成」を続行すると、これらの変更は失われます。\n\n' +
          '問題ない場合は「OK」を押下して操作を続行してください。'
        }
        onConfirm={() => {
          setUnsavedConfirmOpen(false);
          setCopyDialogOpen(true);
        }}
        onCancel={() => setUnsavedConfirmOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 放送開始 / 終了チャイム セレクタ (要件 3・4)
// ---------------------------------------------------------------------------

interface ChimeSelectFieldProps {
  /** `'start'` = 放送開始チャイム / `'end'` = 放送終了チャイム。data-testid の接頭辞にも使う。 */
  role: 'start' | 'end';
  /** 表示ラベル (例: 「放送開始チャイム（任意）」) */
  label: string;
  /** 現在選択されている chime_id (空文字 = 「(なし)」= 鳴らさない) */
  value: string;
  /** チャイムマスタ一覧 (`GET /chimes`) */
  chimes: Chime[];
  /** この役割のチャイムを現在試聴処理中かどうか (ボタンの多重押下抑止) */
  previewing: boolean;
  /** 試聴機能が利用可能か (親が onPreviewChime を渡しているか) */
  canPreview: boolean;
  /** 選択変更時のハンドラ (空文字 = 「(なし)」) */
  onChange: (chimeId: string) => void;
  /** 試聴ボタン押下時のハンドラ */
  onPreview: () => void;
}

/**
 * 放送開始 / 終了チャイムの選択フィールド (要件 3・4)。
 *
 * - チャイムマスタ (`GET /chimes`) の一覧をセレクトボックスで提示し、
 *   「(なし)」を選ぶと当該タイミングのチャイムを鳴らさない (既存挙動と同一)。
 * - 選択済みの場合のみ「▶ 試聴」ボタンを有効化し、`GET /chimes/{chime_id}/audio`
 *   で取得した音源を再生できる。
 * - チャイムマスタが空 (未登録) の場合はセレクトを disabled にし、
 *   「チャイムマスタ画面で登録してください」と案内する
 *   (選択肢が無いのに操作できると迷わせるため)。
 */
function ChimeSelectField({
  role,
  label,
  value,
  chimes,
  previewing,
  canPreview,
  onChange,
  onPreview,
}: ChimeSelectFieldProps): React.JSX.Element {
  const hasChimes = chimes.length > 0;
  return (
    <label className="form-grid__group">
      <span>{label}</span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select
          data-testid={`${role}-chime-select`}
          aria-label={label}
          value={value}
          disabled={!hasChimes}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="">（なし / 鳴らさない）</option>
          {chimes.map((chime) => (
            <option key={chime.chime_id} value={chime.chime_id}>
              {chime.label ?? chime.chime_id}
            </option>
          ))}
        </select>
        {canPreview ? (
          <button
            type="button"
            data-testid={`${role}-chime-preview-button`}
            aria-label={`${label}を試聴`}
            title="選択したチャイム音を再生します"
            disabled={!value || previewing}
            onClick={onPreview}
          >
            {previewing ? '再生中...' : '▶ 試聴'}
          </button>
        ) : null}
      </div>
      <span className="hint" style={{ fontSize: 12, color: '#555' }}>
        {hasChimes
          ? role === 'start'
            ? 'この放送地点の放送キューの先頭に鳴らすチャイム音です。「（なし）」で鳴りません。'
            : 'この放送地点の放送キューの末尾に鳴らすチャイム音です。「（なし）」で鳴りません。'
          : 'チャイム音が未登録です。チャイムマスタ画面で音源を登録すると選択できます。'}
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// コピーして新規作成 ダイアログ
// ---------------------------------------------------------------------------

interface CopyDialogProps {
  /** 線区マスタ。空のときは線区/方面ドロップダウンが空になり、決定ボタンが押せない。 */
  routes: RouteMaster[];
  /** コピー元の routeDirection (`${routeId}#${directionName}` 形式)。初期選択値として使う。 */
  sourceRouteDirection: string;
  /** ユーザーが「コピーして新規作成」を確定したとき、選択された routeDirection を返す。 */
  onConfirm: (routeDirection: string) => void;
  /** ダイアログを閉じる (キャンセル / 背景クリック / × ボタン)。 */
  onClose: () => void;
}

/**
 * 「コピーして新規作成」確認ダイアログ。
 *
 * - 編集モーダルの上に重ねて表示する小さなモーダル。
 * - コピー元の線区/方面を初期選択した状態で開き、ユーザーが別の線区/方面に変更できる。
 * - 線区/方面の両方が選択されるまで実行ボタンは押せない。
 */
function CopyDialog({
  routes,
  sourceRouteDirection,
  onConfirm,
  onClose,
}: CopyDialogProps): React.JSX.Element {
  const initial = useMemo(() => parseRouteDirection(sourceRouteDirection), [sourceRouteDirection]);
  const [routeId, setRouteId] = useState(initial.routeId);
  const [directionName, setDirectionName] = useState(initial.directionName);

  const selectedRoute = useMemo(
    () => routes.find((r) => r.route_id === routeId),
    [routes, routeId],
  );
  const directions = selectedRoute?.directions ?? [];

  const canConfirm = Boolean(routeId) && Boolean(directionName);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="コピーして新規作成"
      data-testid="copy-dialog"
      style={{ zIndex: 1200 }}
    >
      <div className="modal modal--single-column" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>📋 コピーして新規作成</h2>
          <button
            type="button"
            data-testid="copy-dialog-close-button"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            <p style={{ margin: '0 0 12px' }}>
              現在の内容を引き継いで新規の放送地点を作成します。
              <br />
              コピー先の<strong>線区・方面</strong>を選択してください。
            </p>
            <label>
              <span className="required">線区</span>
              <select
                data-testid="copy-dialog-route-select"
                aria-label="コピー先の線区"
                value={routeId}
                onChange={(e) => {
                  // 線区変更時は方面を一旦リセットする (元と同じ方面が無いケースに対応)
                  setRouteId(e.target.value);
                  setDirectionName('');
                }}
              >
                <option value="">(選択してください)</option>
                {routes.map((r) => (
                  <option key={r.route_id} value={r.route_id}>
                    {r.route_name} ({r.route_id})
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="required">方面</span>
              <select
                data-testid="copy-dialog-direction-select"
                aria-label="コピー先の方面"
                value={directionName}
                onChange={(e) => setDirectionName(e.target.value)}
                disabled={!routeId}
              >
                <option value="">(選択してください)</option>
                {directions.map((d) => (
                  <option key={d.direction_name} value={d.direction_name}>
                    {d.direction_name}
                  </option>
                ))}
              </select>
            </label>
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#555' }}>
              引き継がれる項目: 半径・放送遅延・コンテンツ
              <br />
              クリアされる項目: 説明・緯度・経度
              <br />
              ※モーダル内で編集中の未保存の変更は失われます。
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" data-testid="copy-dialog-cancel-button" onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            data-testid="copy-dialog-confirm-button"
            className="primary"
            disabled={!canConfirm}
            onClick={() => {
              if (!canConfirm) return;
              onConfirm(toRouteDirection(routeId, directionName));
            }}
          >
            コピーして新規作成
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 地図ポップアップ
// ---------------------------------------------------------------------------

interface MapPopupProps {
  /**
   * 編集モーダル本体への ref。
   * ポップアップを「モーダルと同じ大きさ・同じ位置」に重ねるために、開いた時点で
   * `getBoundingClientRect()` により実寸を計測する。
   */
  modalRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  editingLatitude: number | undefined;
  editingLongitude: number | undefined;
  editingRadiusMeters: number;
  otherPoints: BroadcastPoint[];
  routeShapePoints: RouteShapePointsFile | null;
  onLocationChange: (lat: number, lng: number) => void;
}

function MapPopup({
  modalRef,
  onClose,
  editingLatitude,
  editingLongitude,
  editingRadiusMeters,
  otherPoints,
  routeShapePoints,
  onLocationChange,
}: MapPopupProps): React.JSX.Element {
  /**
   * ポップアップの位置 + サイズ。
   * 初期値はモーダル本体の実寸 (= 同じ大きさ・同じ位置) で、以降ヘッダーの
   * ドラッグやウィンドウ端の `resize: both` でユーザーが自由に変更できる。
   */
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  // ドラッグ中かどうかを管理する ref (state にすると再レンダリングが多発するため ref を使う)
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);

  /**
   * 初期サイズ・位置: 編集モーダル本体と同じ矩形に合わせる。
   *
   * 従来は「地図を開く」ボタン直下に 520px 幅の小さなポップアップを出していたが、
   * 地図が狭くて位置を選びにくいという指摘があったため、モーダルと同サイズに変更した。
   * モーダルがまだ計測できない (幅 0 = jsdom 等) 場合はビューポート基準の
   * フォールバック値を使う。
   */
  useEffect(() => {
    const modalRect = modalRef.current?.getBoundingClientRect();
    if (modalRect && modalRect.width > 0 && modalRect.height > 0) {
      setRect({
        top: modalRect.top,
        left: modalRect.left,
        width: modalRect.width,
        height: modalRect.height,
      });
      return;
    }
    setRect(fallbackPopupRect());
  }, [modalRef]);

  // Esc キーで地図を閉じる (座標は入力欄に残る)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ドラッグ開始
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    // ヘッダー内のボタンはドラッグ対象外
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    if (rect === null) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: rect.left,
      origTop: rect.top,
    };
  };

  // ドラッグ中 (window に登録して要素外でも追従)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const newLeft = Math.max(0, dragRef.current.origLeft + dx);
      const newTop = Math.max(0, dragRef.current.origTop + dy);
      setRect((r) => (r ? { ...r, left: newLeft, top: newTop } : r));
    };

    const handleMouseUp = (): void => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const popupStyle: React.CSSProperties = {
    position: 'fixed',
    top: rect?.top ?? -9999,
    left: rect?.left ?? -9999,
    width: rect?.width ?? 0,
    height: rect?.height ?? 0,
    zIndex: 1100,
    resize: 'both',
    overflow: 'hidden',
    // 初期サイズ・位置が決まるまで非表示
    visibility: rect ? 'visible' : 'hidden',
  };

  /** 位置が指定済みか (緯度・経度の両方が確定しているか) */
  const hasLocation = editingLatitude !== undefined && editingLongitude !== undefined;

  return (
    <div className="map-popup" style={popupStyle} role="dialog" aria-label="地図で位置を選択">
      <div className="map-popup__header" onMouseDown={handleMouseDown} style={{ cursor: 'grab' }}>
        <span className="map-popup__title">📍 地図で位置を選択</span>
        <button
          type="button"
          className="map-popup__close"
          aria-label="地図を閉じる"
          title="地図を閉じます（選択した座標は入力欄に残ります）"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      {/* 操作方法のヒント。座標が即時反映されることを明示する */}
      <p className="map-popup__hint">
        地図をクリック、またはピンをドラッグして位置を指定してください（座標は入力欄へ即時反映されます）
      </p>
      <div className="map-popup__body">
        <BroadcastPointsMap
          editingLatitude={editingLatitude}
          editingLongitude={editingLongitude}
          editingRadiusMeters={editingRadiusMeters}
          otherPoints={otherPoints}
          routeShapePoints={routeShapePoints}
          onLocationChange={onLocationChange}
        />
      </div>
      {/*
        フッター。
        従来は「× で閉じる」しか選択肢が無く、位置を選んだ後どうすればよいのか
        分かりにくかった。現在の選択座標と「この位置を使う」ボタンを常時表示して
        次の操作を明示する (押しても保存は行われず、地図を閉じるだけ)。
      */}
      <div className="map-popup__footer">
        {hasLocation ? (
          <span className="map-popup__coords" data-testid="map-popup-coords">
            緯度 {editingLatitude?.toFixed(6)} / 経度 {editingLongitude?.toFixed(6)}
          </span>
        ) : (
          <span
            className="map-popup__coords map-popup__coords--empty"
            data-testid="map-popup-coords"
          >
            未指定（地図をクリックして位置を選んでください）
          </span>
        )}
        <button
          type="button"
          className="primary"
          data-testid="map-popup-confirm-button"
          aria-label="この位置を使う"
          title="地図で指定した緯度・経度を入力欄に反映して地図を閉じます（データの保存は編集画面の「保存」ボタンで行います）"
          disabled={!hasLocation}
          onClick={onClose}
        >
          この位置を使う
        </button>
      </div>
    </div>
  );
}

function normalize(v: BroadcastPointFormValues): BroadcastPointFormValues {
  return {
    routeDirection: v.routeDirection,
    spotId: v.spotId,
    description: v.description ?? '',
    latitude: v.latitude,
    longitude: v.longitude,
    radius_meters: v.radius_meters || DEFAULT_RADIUS,
    broadcast_delay_seconds: v.broadcast_delay_seconds ?? 0,
    // route_nickname はフォーム内では常に string で扱う (未設定は空文字)。
    // 保存時に親側 (BroadcastPointsPage) で空文字→undefined に正規化する。
    route_nickname: typeof v.route_nickname === 'string' ? v.route_nickname : '',
    // voice_id もフォーム内では常に string 保持 (未設定 = 空文字 =
    // サーバー側デフォルト Takumi にフォールバック)。保存時に親側で空文字→undefined に正規化する。
    voice_id: typeof v.voice_id === 'string' ? v.voice_id : '',
    // 要件 3・4: 放送開始 / 終了チャイム。未設定は空文字 (= 鳴らさない)。
    start_chime_id: typeof v.start_chime_id === 'string' ? v.start_chime_id : '',
    end_chime_id: typeof v.end_chime_id === 'string' ? v.end_chime_id : '',
    contents: Array.isArray(v.contents) ? v.contents : [],
  };
}

/**
 * 緯度経度から駅間ラベル (「開始駅-終了駅」) を判定するピュア関数。
 * - 緯度経度が undefined / NaN の場合や、駅順マスタが空の場合は null。
 * - 該当する駅間が判定できない場合も null。
 *
 * BroadcastPointForm の自動入力ロジックから抽出してテスト可能にしたもの。
 */
export function computeStationSegmentLabel(
  routeStops: Record<string, RouteStop[]> | undefined,
  routeDirection: string,
  latitude: number | undefined,
  longitude: number | undefined,
): string | null {
  if (
    latitude === undefined ||
    longitude === undefined ||
    Number.isNaN(latitude) ||
    Number.isNaN(longitude)
  ) {
    return null;
  }
  const { routeId } = parseRouteDirection(routeDirection);
  const segment = findStationSegment(routeStops?.[routeId], latitude, longitude);
  const label = formatStationSegmentLabel(segment);
  return label || null;
}

/** 入力値のバリデーション。エラー文言を返す (null = 問題なし) */
export function validate(values: BroadcastPointFormValues): string | null {
  if (!values.routeDirection || !values.routeDirection.includes('#')) {
    return 'routeDirection は "<線区>#<方面>" 形式で必須です';
  }
  if (!values.description || values.description.trim() === '') {
    return '説明を入力してください';
  }
  if (values.latitude === undefined || Number.isNaN(values.latitude)) {
    return '緯度を入力してください';
  }
  if (values.longitude === undefined || Number.isNaN(values.longitude)) {
    return '経度を入力してください';
  }
  if (values.latitude < -90 || values.latitude > 90) {
    return '緯度は -90 〜 90 の範囲で指定してください';
  }
  if (values.longitude < -180 || values.longitude > 180) {
    return '経度は -180 〜 180 の範囲で指定してください';
  }
  if (values.radius_meters <= 0) {
    return '半径は 1 メートル以上で指定してください';
  }
  if (values.broadcast_delay_seconds < 0) {
    return '放送遅延は 0 以上で指定してください';
  }
  // コンテンツ毎のバリデーション
  for (let i = 0; i < values.contents.length; i++) {
    const err = validateContent(values.contents[i] as BroadcastContent, i);
    if (err) return err;
  }
  return null;
}
